/**
 * AssessmentUpload - Upload standardized test results
 * 
 * Streamlined flow:
 * 1. Select test type
 * 2. Drop file
 * 3. AI parses & extracts everything (skills, test window, grade level)
 * 4. Parent confirms estimated age (auto-calculated from grade)
 * 5. Upload
 */

import { useState, useRef, useCallback } from 'react';
import { PersonalizedPreview } from './PersonalizedPreview';

// Types
type AssessmentSource = 'i-ready' | 'map-growth' | 'sat-10' | 'scat';

interface SkillVector {
  number_sense: number;
  ratios_rates: number;
  linear_relationships: number;
  functions: number;
  geometry: number;
  data_probability: number;
}

interface ParsedEvidence {
  source: AssessmentSource;
  skillVector: SkillVector;
  uncertainty: number;
  confidence: number;
  rawExtraction?: {
    overall_placement?: string;
    overall_percentile?: number;
    test_window?: string;
    grade_level?: number;
    domains?: Record<string, unknown>;
    extraction_notes?: string;
  };
}

interface AssessmentUploadProps {
  childId: string;
  childName: string;
  onSuccess?: (data: unknown) => void;
  onClose?: () => void;
}

// API base
const API_BASE = import.meta.env.VITE_API_URL || 'https://goalblox-roblox-api-667990366434.us-central1.run.app';

// Test configurations
const TEST_CONFIGS: Record<AssessmentSource, {
  name: string;
  description: string;
  icon: string;
}> = {
  'i-ready': {
    name: 'i-Ready',
    description: 'Upload your i-Ready Math Diagnostic report',
    icon: '📊',
  },
  'map-growth': {
    name: 'MAP Growth',
    description: 'Upload your NWEA MAP Growth report',
    icon: '📈',
  },
  'sat-10': {
    name: 'SAT-10',
    description: 'Upload your Stanford Achievement Test report',
    icon: '📝',
  },
  'scat': {
    name: 'SCAT',
    description: 'Upload your School and College Ability Test results',
    icon: '🎯',
  },
};

// US average age by grade (in months, mid-year)
const GRADE_TO_AGE_MONTHS: Record<number, number> = {
  0: 66,    // Kindergarten: ~5.5 years
  1: 78,    // Grade 1: ~6.5 years
  2: 90,    // Grade 2: ~7.5 years
  3: 102,   // Grade 3: ~8.5 years
  4: 114,   // Grade 4: ~9.5 years
  5: 126,   // Grade 5: ~10.5 years
  6: 138,   // Grade 6: ~11.5 years
  7: 150,   // Grade 7: ~12.5 years
  8: 162,   // Grade 8: ~13.5 years
  9: 174,   // Grade 9: ~14.5 years
  10: 186,  // Grade 10: ~15.5 years
  11: 198,  // Grade 11: ~16.5 years
  12: 210,  // Grade 12: ~17.5 years
};

// Extract grade number from any string containing "Grade X" pattern
function extractGradeFromString(text: string | undefined): number | null {
  if (!text) return null;
  const match = text.match(/grade\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : null;
}

// Extract grade from rawExtraction - check multiple possible locations
function extractGradeFromRawExtraction(rawExt: Record<string, any> | undefined): number | null {
  if (!rawExt) return null;
  
  // First, check if Gemini extracted it directly
  if (typeof rawExt.student_grade === 'number') {
    return rawExt.student_grade;
  }
  
  // Try various locations where grade might be mentioned as text
  const candidates = [
    rawExt.overall_placement,
    rawExt.most_recent_test?.placement,
    rawExt.most_recent_test?.overall_placement,
    rawExt.most_recent_test?.badge,
    rawExt.badge,
    rawExt.extraction_notes,
    // Check domains for grade mentions
    rawExt.domains?.number_operations?.raw_text,
    rawExt.domains?.geometry?.raw_text,
    // Check tests array
    rawExt.tests?.[rawExt.tests.length - 1]?.placement,
  ];
  
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const grade = extractGradeFromString(candidate);
      if (grade !== null) return grade;
    }
  }
  
  return null;
}

// Convert months to years string
function monthsToYearsString(months: number): string {
  const years = months / 12;
  return `${years.toFixed(1)} years`;
}

// Get current test window
function getCurrentTestWindow(): string {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  
  const academicYear = month >= 7 
    ? `${year}-${(year + 1).toString().slice(2)}` 
    : `${year - 1}-${year.toString().slice(2)}`;
  
  const season = month >= 8 && month <= 11 ? 'Fall' :
                 month >= 0 && month <= 2 ? 'Winter' :
                 month >= 3 && month <= 5 ? 'Spring' : 'Summer';
  
  return `${academicYear} ${season}`;
}

// Convert test date string to test_window format
function dateToTestWindow(dateStr: string): string {
  let month: number | null = null;
  let year: number | null = null;
  
  // Try MM/DD/YY format
  const slashMatch = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (slashMatch) {
    month = parseInt(slashMatch[1], 10);
    year = parseInt(slashMatch[3], 10);
    if (year < 100) year += 2000;
  }
  
  if (!month || !year) {
    return getCurrentTestWindow();
  }
  
  // Determine academic year (Aug-Jul cycle)
  const academicYearStart = month >= 8 ? year : year - 1;
  const academicYear = `${academicYearStart}-${(academicYearStart + 1).toString().slice(2)}`;
  
  // Determine season
  const season = month >= 8 && month <= 11 ? 'Fall' :
                 (month >= 12 || month <= 2) ? 'Winter' :
                 month >= 3 && month <= 5 ? 'Spring' : 'Summer';
  
  return `${academicYear} ${season}`;
}

export function AssessmentUpload({ 
  childId, 
  childName, 
  onSuccess,
  onClose,
}: AssessmentUploadProps) {
  const [source, setSource] = useState<AssessmentSource>('i-ready');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedEvidence, setParsedEvidence] = useState<ParsedEvidence | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Auto-calculated values (editable by parent if needed)
  const [estimatedAgeMonths, setEstimatedAgeMonths] = useState<number>(108); // Default 9 years
  const [testWindow, setTestWindow] = useState<string>(getCurrentTestWindow());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const config = TEST_CONFIGS[source];

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [source]);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setParsedEvidence(null);
    setParseError(null);

    setIsParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source', source);

      const response = await fetch(`${API_BASE}/api/assessments/parse`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Parse failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setParsedEvidence(data.evidence);
      
      // Auto-extract test window if available
      // Check both test_window and test_date
      const rawExt = data.evidence?.rawExtraction;
      if (rawExt?.test_window) {
        setTestWindow(rawExt.test_window);
      } else if (rawExt?.test_date) {
        // Convert test_date to test_window
        setTestWindow(dateToTestWindow(rawExt.test_date));
      }
      
      // Auto-estimate age from grade level at time of test
      const extractedGrade = extractGradeFromRawExtraction(rawExt);
      if (extractedGrade !== null && GRADE_TO_AGE_MONTHS[extractedGrade]) {
        setEstimatedAgeMonths(GRADE_TO_AGE_MONTHS[extractedGrade]);
        console.log(`📊 Extracted grade ${extractedGrade} → age ${GRADE_TO_AGE_MONTHS[extractedGrade]} months at test time`);
      } else {
        console.log('⚠️ Could not extract grade from:', rawExt);
      }
      
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse');
    } finally {
      setIsParsing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (!file) throw new Error('Please upload a file');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', source);
      formData.append('child_id', childId);
      formData.append('age_months_at_test', String(estimatedAgeMonths));
      formData.append('test_window', testWindow);

      const response = await fetch(`${API_BASE}/api/assessments/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSuccess(true);
      onSuccess?.(data);

      // Don't auto-close - let user see the personalized preview

    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-blox-card border border-blox-surface overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blox-accent/20 to-blox-xp/20 p-6 border-b border-blox-surface sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl text-white flex items-center gap-2">
              <span>📋</span> Upload Assessment
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Import {childName}'s standardized test results
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-blox-surface transition-colors">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {success && parsedEvidence ? (
        /* Success: Show personalized preview */
        <div className="p-6">
          <PersonalizedPreview
            childName={childName}
            skillVector={parsedEvidence.skillVector}
            assessedGradeLevel={(() => {
              // Use assessed performance, not actual grade
              // Skill vector values map: 0.36 = Grade 2, 0.48 = Grade 3, etc.
              const avgSkill = Object.values(parsedEvidence.skillVector).reduce((a, b) => a + b, 0) / 6;
              // Convert skill value to grade: 0.12 = K, 0.24 = 1, 0.36 = 2, etc.
              return Math.round(avgSkill / 0.12);
            })()}
            onClose={onClose}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Test Type */}
        <div>
          <label className="block text-sm font-semibold text-slate-400 mb-3">Assessment Type</label>
          <div className="grid grid-cols-4 gap-2">
            {(Object.keys(TEST_CONFIGS) as AssessmentSource[]).map((key) => {
              const tc = TEST_CONFIGS[key];
              const isSelected = source === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setSource(key); setFile(null); setParsedEvidence(null); setParseError(null); }}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    isSelected
                      ? 'bg-blox-accent/20 border-blox-accent text-white'
                      : 'bg-blox-surface/50 border-blox-surface hover:border-slate-600 text-slate-400'
                  }`}
                >
                  <span className="text-xl block mb-1">{tc.icon}</span>
                  <span className="text-xs font-medium">{tc.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* File Upload */}
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleFileDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-blox-accent bg-blox-accent/10' :
            file ? 'border-blox-xp bg-blox-xp/10' :
            'border-slate-600 hover:border-slate-500'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className="hidden"
          />

          {isParsing ? (
            <div className="space-y-3">
              <div className="w-12 h-12 mx-auto border-4 border-blox-accent border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-300">🤖 AI is reading your document...</p>
              <p className="text-xs text-slate-500">Extracting skills, grade level, test date...</p>
            </div>
          ) : file ? (
            <div className="space-y-2">
              <span className="text-4xl">✅</span>
              <p className="text-white font-medium">{file.name}</p>
              <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB • Click to replace</p>
            </div>
          ) : (
            <div className="space-y-2">
              <span className="text-4xl">📄</span>
              <p className="text-slate-300">Drop your {config.name} report here</p>
              <p className="text-xs text-slate-500">PDF or image • AI extracts everything automatically</p>
            </div>
          )}
        </div>

        {/* Parse Error */}
        {parseError && (
          <div className="p-4 rounded-xl bg-blox-error/10 border border-blox-error/30 text-blox-error text-sm">
            <strong>Parse Error:</strong> {parseError}
          </div>
        )}

        {/* Parsed Results */}
        {parsedEvidence && (
          <div className="space-y-4">
            {/* Skill bars */}
            <div className="p-4 rounded-xl bg-blox-xp/10 border border-blox-xp/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-blox-xp flex items-center gap-2">
                  <span>✨</span> Extracted Skills
                </h4>
                <span className="text-xs text-slate-400">
                  {Math.round(parsedEvidence.confidence * 100)}% confident
                </span>
              </div>
              
              <div className="space-y-2">
                {Object.entries(parsedEvidence.skillVector).map(([skill, value]) => (
                  <div key={skill} className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 w-28 truncate capitalize">{skill.replace(/_/g, ' ')}</span>
                    <div className="flex-1 h-2 bg-blox-dark rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          value >= 0.8 ? 'bg-blox-xp' : 
                          value >= 0.5 ? 'bg-blox-accent' : 
                          'bg-blox-gold'
                        }`}
                        style={{ width: `${value * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-300 w-10 text-right">{Math.round(value * 100)}%</span>
                  </div>
                ))}
              </div>

              {parsedEvidence.rawExtraction?.extraction_notes && (
                <p className="mt-3 text-xs text-slate-500 italic">
                  💡 {parsedEvidence.rawExtraction.extraction_notes}
                </p>
              )}
            </div>

            {/* Auto-extracted metadata (editable) */}
            <div className="p-4 rounded-xl bg-blox-surface/50 border border-blox-surface">
              <h4 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                <span>📝</span> Confirm Details
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Test Window */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Test Window</label>
                  <input
                    type="text"
                    value={testWindow}
                    onChange={(e) => setTestWindow(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-blox-dark border border-slate-700 text-white text-sm focus:outline-none focus:border-blox-accent"
                  />
                </div>
                
                {/* Estimated Age at Test */}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Estimated Age at Test <span className="italic text-slate-600">(from grade)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={estimatedAgeMonths}
                      onChange={(e) => setEstimatedAgeMonths(parseInt(e.target.value, 10) || 108)}
                      min={48}
                      max={240}
                      className="w-20 px-3 py-2 rounded-lg bg-blox-dark border border-slate-700 text-white text-sm focus:outline-none focus:border-blox-accent"
                    />
                    <span className="text-sm text-slate-400 italic">
                      months ({monthsToYearsString(estimatedAgeMonths)})
                    </span>
                  </div>
                </div>
              </div>
              
              {parsedEvidence.rawExtraction?.overall_placement && (
                <p className="mt-2 text-xs text-slate-500">
                  Placement: <span className="text-white">{parsedEvidence.rawExtraction.overall_placement}</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Errors */}
        {submitError && (
          <div className="p-4 rounded-xl bg-blox-error/10 border border-blox-error/30 text-blox-error text-sm">
            {submitError}
          </div>
        )}

        {/* Submit */}
        <div className="flex gap-3">
          {onClose && (
            <button type="button" onClick={onClose}
              className="px-6 py-3 rounded-xl border border-slate-600 text-slate-400 font-semibold hover:bg-blox-surface transition-colors">
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={!file || !parsedEvidence || isSubmitting || isParsing}
            className="flex-1 px-6 py-3 rounded-xl bg-blox-accent text-blox-dark font-semibold hover:bg-blox-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? '⏳ Uploading...' : '🚀 Upload Assessment'}
          </button>
        </div>

        <p className="text-xs text-slate-500 text-center">
          🔒 Documents analyzed by AI and immediately discarded. Only skill levels are saved.
        </p>
      </form>
      )}
    </div>
  );
}

export default AssessmentUpload;
